import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, finalize, map, Observable, of, shareReplay, tap } from 'rxjs';
import { BaseService } from '../../../core/base/base.service';
import { Searchable } from '../../../shared/interfaces/searchable.model';
import { ISelectOption } from '../../../shared/interfaces/select-option.interface';
import { BaseColorModel } from '../../../shared/models/base-color.model';

@Injectable({
  providedIn: 'root',
})
export class BaseColorService extends BaseService {
  private baseColors$ = new BehaviorSubject<Searchable<BaseColorModel>>(
    new Searchable<BaseColorModel>()
  );

  /** The load in flight, so every select mounting at once shares a single request. */
  private baseColorsRequest$: Observable<Searchable<BaseColorModel>> | null = null;

  constructor(
    http: HttpClient,
    private translate: TranslateService
  ) {
    super(http, 'base_color', '/base-color', BaseColorModel);

    // Base-color names are cached in `baseColors$`; re-fetch on language switch so the
    // shared `baseColors$` emits new-language options and every subscribed select updates.
    this.translate.onLangChange.pipe(takeUntilDestroyed()).subscribe(() => {
      if (this.baseColors$.value?.options?.length) {
        this.getAllForSelect().subscribe();
      }
    });
  }

  getBaseColors(): Observable<Searchable<BaseColorModel>> {
    return this.baseColors$.asObservable();
  }

  /**
   * The cache when it is filled, otherwise a single shared load of it — the same shape as
   * `CountryService.ensureCountriesLoaded`.
   *
   * Consumers must go through this rather than trust the app initializer alone: that fetch is
   * skipped when the session starts on the login route (a login through the form navigates
   * client-side, so the initializer never runs again) and is fire-and-forget when it does
   * run, so a failed load left every select empty with nothing to retry it. A failed request
   * clears the in-flight slot, so the next caller tries again.
   */
  ensureBaseColorsLoaded(): Observable<Searchable<BaseColorModel>> {
    const current = this.baseColors$.value;

    if (current?.options?.length) {
      return of(current);
    }

    if (this.baseColorsRequest$) {
      return this.baseColorsRequest$;
    }

    this.baseColorsRequest$ = this.getAllForSelect().pipe(
      finalize(() => {
        this.baseColorsRequest$ = null;
      }),
      shareReplay(1)
    );

    return this.baseColorsRequest$;
  }

  getAllForSelect(): Observable<Searchable<BaseColorModel>> {
    return this.http
      .get<Searchable<BaseColorModel>>(`${this.uri}/selectAll`, {
        headers: this.defaultHeaders,
        params: this.withLang(),
      })
      .pipe(
        map((response: any) => {
          const data = response.data.map((row: any) => new this.Model(row));
          const select = data.map((row: any) => row?.toSelect() || row);
          return { ...response, data, select };
        }),
        tap((response: any) => {
          const baseColorsResp = new Searchable<BaseColorModel>();
          baseColorsResp.update(response, 1);
          return this.baseColors$.next(baseColorsResp);
        })
      );
  }

  getBaseColorsForSelect(search?: string): Observable<Searchable<BaseColorModel>> {
    return this.getBaseColors().pipe(
      map((searchable) => {
        if (!search) {
          return searchable;
        }

        const filtered = searchable.options.filter((option: ISelectOption) => {
          return option.label.toLowerCase().includes(search.toLowerCase());
        });

        const result = new Searchable<BaseColorModel>();
        result.update(filtered, 1);
        return result;
      })
    );
  }
}
